<script setup lang="ts">
import type { ISearchResultCard } from '../model/types/searchResultCard';
import type { IBadgeItems } from '@/shared/ui/Badge/types';
import { PRODUCT_IMAGE_PLACEHOLDER_PATH } from '@/shared/constants/placeholders';
import Badge from '@/shared/ui/Badge/index.vue';
import ProductCartButton from '@/features/ProductCartButton/index.vue';
import { generateProductDetailLink } from '@/shared/utils/generateProductDetailLink';
import { MIN_BOUGHT_AMOUNT } from '@/shared/constants/badges';
import { useCategoriesStore } from '@/entities/categories/index';

const { product } = defineProps<ISearchResultCard>();

const categoriesStore = useCategoriesStore();
const { locale } = useI18n();
const localePath = useLocalePath();

const badges: Ref<IBadgeItems[]> = ref([
  {
    id: 1,
    text: 'Ликвидация',
    type: 'liquidation',
    isShow: Boolean(product.liquidation),
  },
  {
    id: 2,
    text: String(product.bought_count),
    type: 'bought',
    isShow: Boolean(product.bought_count > MIN_BOUGHT_AMOUNT),
  },
  {
    id: 3,
    text: '50',
    type: 'remained',
    isShow: Boolean(product.in_stock),
  },
  {
    id: 4,
    text: 'Новинка',
    type: 'new_arrival',
    isShow: Boolean(product.new_arrival),
  },
]);

const cardLink = computed(() =>
  generateProductDetailLink(
    product,
    categoriesStore.categories,
    locale.value,
    localePath,
  ),
);
</script>

<template>
  <article class="search-result-card">
    <NuxtLink :to="cardLink" class="search-result-card__link">
      <div class="search-result-card__image-wrapper">
        <NuxtImg
          class="search-result-card__image"
          :src="product.images[0]?.image"
          :alt="product.name"
          loading="lazy"
          :placeholder="PRODUCT_IMAGE_PLACEHOLDER_PATH"
        />
      </div>
      <div class="search-result-card__content">
        <div class="search-result-card__title">
          <span>{{ product.name }}</span>
        </div>
        <div class="search-result-card__price-wrapper">
          <span class="search-result-card__price _new">{{ product.price }} ₽</span>
          <span class="search-result-card__price _old"
            >{{ product.price_with_discount }} ₽</span
          >
        </div>
        <ul class="search-result-card__badges">
          <li
            v-for="badge in badges"
            :key="badge.id"
            v-show="badge.isShow"
            class="search-result-card__badge"
          >
            <Badge :id="badge.id" :text="badge?.text" :type="badge?.type" />
          </li>
        </ul>
      </div>
    </NuxtLink>
    <div class="search-result-card__action">
      <ProductCartButton :product="product" />
    </div>
  </article>
</template>

<style lang="less">
.search-result-card {
  .flex-column(@size-x4);

  align-items: flex-end;

  &__link {
    display: flex;
    gap: 12px;
    width: 100%;
  }

  &__image-wrapper {
    max-width: 120px;
    height: 100px;
    width: 100%;
    border-radius: 24px;
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
  }

  &__title {
    font-size: 16px;
    line-height: 130%;
    font-weight: 500;
    color: @color-mine-shaft;
  }

  &__price-wrapper {
    display: inline-flex;
    align-items: center;
    gap: @size-x3;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: @size-x2;
  }

  &__price {
    display: inline-flex;
    font-size: 17px;
    line-height: 130%;
    font-weight: 700;
    color: @color-mine-shaft;

    &._old {
      font-size: 15px;
      color: @color-silver-chalice;
      text-decoration: line-through;
      text-decoration-thickness: 2px;
    }

    &._new {
      color: @color-mona-lisa;
    }
  }

  &__badges {
    display: flex;
    flex-wrap: wrap;
    gap: @size-x2;
  }

  &__action {
    min-width: 180px;
  }

  @media @1024 {
    flex-direction: row;
    align-items: flex-start;
    gap: @size-x4;
    box-shadow: 0px 5px 9px 0px rgba(0, 0, 0, 0.04);
    padding: @size-x4;
    border-radius: 16px;
    position: relative;
    padding-bottom: 64px;

    &__image-wrapper {
      max-width: 60px;
      height: 60px;
    }

    &__action {
      margin-left: auto;
      position: absolute;
      bottom: 12px;
      width: calc(100% - 24px);
      left: 12px;
    }
  }
}
</style>
